import { useEffect, useRef, useState } from 'react';
import type { TrackPointRow } from 'shared';
import { addThemedTileLayer, loadLeaflet } from './leaflet';

interface Props {
  points: TrackPointRow[];
  height?: number;
}

export default function TrackMap({ points, height = 240 }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const layer = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => {
        if (cancelled || !box.current || points.length === 0) return;
        const L = window.L;

        if (!map.current) {
          map.current = L.map(box.current, { zoomControl: true });
          addThemedTileLayer(map.current);
        }

        // Слой пересоздаём целиком: точек десятки, дешевле перерисовать,
        // чем сверять с предыдущим состоянием.
        layer.current?.remove();
        layer.current = L.layerGroup().addTo(map.current);

        const latlngs = points.map((p) => [p.lat, p.lng] as [number, number]);
        L.polyline(latlngs, { weight: 4, opacity: 0.8 }).addTo(layer.current);

        const last = points[points.length - 1]!;
        L.circleMarker([last.lat, last.lng], {
          radius: 8,
          weight: 3,
          fillOpacity: 1,
        })
          .bindTooltip(
            `Машина здесь · ${new Date(last.recordedAt).toLocaleString('ru-RU')}`,
          )
          .addTo(layer.current);

        if (latlngs.length === 1) map.current.setView(latlngs[0], 11);
        else map.current.fitBounds(L.latLngBounds(latlngs), { padding: [24, 24] });

        // Карта строится в скрытом блоке и не знает своих размеров,
        // пока его не показали. Без этого видно один серый квадрат.
        setTimeout(() => map.current?.invalidateSize(), 60);
      })
      .catch((e: Error) => !cancelled && setError(e.message));

    return () => {
      cancelled = true;
    };
  }, [points]);

  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );

  if (points.length === 0) {
    return <div className="meta">Точек ещё нет — водитель не включил трансляцию.</div>;
  }
  if (error) return <div className="meta">{error}</div>;

  return <div ref={box} className="trackmap" style={{ height }} />;
}
